import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  type AbstractControl,
  FormControl,
  FormGroup,
  FormRecord,
  ReactiveFormsModule,
  type ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import type {
  AccommodationCreateOrUpdateRequest,
  AccommodationDetail,
  AccommodationName,
} from '@models/accommodations';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmAccommodationsService } from '@services/crm-accommodations.service';
import { CrmWatersService } from '@services/crm-waters.service';
import { ToastService } from '@services/toast.service';
import { BreadcrumbComponent, type BreadcrumbItem } from '@shared/components/breadcrumb/breadcrumb.component';
import { ImagePickerComponent } from '@shared/components/image-picker/image-picker.component';
import { LocalizedItemEditorComponent } from '@shared/components/localized-item-editor/localized-item-editor.component';
import { LocalizedTextEditorComponent } from '@shared/components/localized-text-editor/localized-text-editor.component';
import { LocationPickerComponent } from '@shared/components/location-picker/location-picker.component';
import { firstValueFrom, of } from 'rxjs';
import { CombinationPricingComponent } from '../shared/combination-pricing/combination-pricing.component';

function locationRequiredValidator(group: AbstractControl): ValidationErrors | null {
  const lat = group.get('latitude')?.value;
  const lng = group.get('longitude')?.value;
  return lat !== 0 || lng !== 0 ? null : { locationRequired: true };
}

interface AccommodationForm {
  number: FormControl<number | null>;
  persons: FormControl<number | null>;
  spots: FormControl<number | null>;
  minimumFishermen: FormControl<number | null>;
  coTravelers: FormControl<number | null>;
  exclusive: FormControl<boolean>;
  isFishingSpot: FormControl<boolean>;
  isActive: FormControl<boolean>;
  dogsAllowed: FormControl<'inherit' | boolean>;
  latitude: FormControl<number>;
  longitude: FormControl<number>;
  descriptionNl: FormControl<string | null>;
  descriptionEn: FormControl<string | null>;
  descriptionDe: FormControl<string | null>;
  descriptionIt: FormControl<string | null>;
  descriptionFr: FormControl<string | null>;
}

@Component({
  selector: 'app-accommodation-form',
  templateUrl: './accommodation-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
    LocationPickerComponent,
    BreadcrumbComponent,
    ImagePickerComponent,
    LocalizedTextEditorComponent,
    LocalizedItemEditorComponent,
    CombinationPricingComponent,
  ],
})
export class AccommodationFormComponent implements OnInit {
  private readonly accommodationsService = inject(CrmAccommodationsService);
  private readonly watersService = inject(CrmWatersService);
  protected readonly codeListsService = inject(CodeListsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private readonly waterIdSig = signal(0);
  private readonly accommodationIdSig = signal<number | null>(null);

  readonly isEditMode = computed(() => this.accommodationIdSig() !== null);
  readonly isSaving = signal(false);

  // Bindings for the combination-pricing section. In create mode the id is 0 until the unit's own
  // save returns; the child then PUTs the grid against the freshly-created id (see save()).
  readonly combinationWaterId = computed(() => this.waterIdSig());
  readonly combinationAccommodationId = computed(() => this.accommodationIdSig() ?? 0);
  readonly isDeleting = signal(false);
  readonly waterTitle = signal('');
  readonly accommodationTitle = signal('');

  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('nav.waters'), routerLink: ['/waters'] },
    {
      label: this.waterTitle() || String(this.waterIdSig()),
      routerLink: ['/waters/edit', this.waterIdSig()],
      queryParams: { tab: 3 },
    },
    {
      label:
        this.isEditMode() && this.accommodationTitle()
          ? this.accommodationTitle()
          : this.translate.instant('waters.accommodations.breadcrumb.create'),
    },
  ]);

  private readonly imagePicker = viewChild(ImagePickerComponent);
  private readonly combinationPricing = viewChild(CombinationPricingComponent);

  readonly pendingImageFile = signal<File | null>(null);
  readonly imageName = signal<string | null>(null);
  readonly imageUrl = signal<string | null>(null);

  readonly waterResource = rxResource({
    params: () => (this.waterIdSig() ? { waterId: this.waterIdSig() } : undefined),
    stream: ({ params }) => this.watersService.getWater(params.waterId),
  });

  readonly waterHintLat = computed(() => parseFloat(this.waterResource.value()?.latitude ?? '') || 0);
  readonly waterHintLng = computed(() => parseFloat(this.waterResource.value()?.longitude ?? '') || 0);

  readonly accommodationResource = rxResource({
    params: () => {
      const aid = this.accommodationIdSig();
      const wid = this.waterIdSig();
      return aid && wid ? { waterId: wid, accommodationId: aid } : undefined;
    },
    stream: ({ params }) => this.accommodationsService.getAccommodation(params.waterId, params.accommodationId),
  });

  readonly isLoading = this.accommodationResource.isLoading;

  readonly form = new FormGroup<AccommodationForm>(
    {
      number: new FormControl<number | null>(null, Validators.required),
      persons: new FormControl<number | null>(null),
      spots: new FormControl<number | null>(null),
      minimumFishermen: new FormControl<number | null>(null),
      coTravelers: new FormControl<number | null>(null),
      exclusive: new FormControl(false, { nonNullable: true }),
      isFishingSpot: new FormControl(false, { nonNullable: true }),
      isActive: new FormControl(true, { nonNullable: true }),
      dogsAllowed: new FormControl<'inherit' | boolean>('inherit', { nonNullable: true }),
      latitude: new FormControl(0, { nonNullable: true }),
      longitude: new FormControl(0, { nonNullable: true }),
      descriptionNl: new FormControl<string | null>(null),
      descriptionEn: new FormControl<string | null>(null),
      descriptionDe: new FormControl<string | null>(null),
      descriptionIt: new FormControl<string | null>(null),
      descriptionFr: new FormControl<string | null>(null),
    },
    { validators: locationRequiredValidator },
  );

  readonly namesGroup = new FormRecord<FormControl<string | null>>({});

  constructor() {
    effect(() => {
      for (const site of this.codeListsService.sites()) {
        const key = String(site.id);
        if (!this.namesGroup.contains(key)) {
          this.namesGroup.addControl(key, new FormControl<string | null>(null));
        }
      }
      const accommodation = this.accommodationResource.value();
      if (accommodation) {
        this.patchNames(accommodation.names);
      }
    });

    effect(() => {
      const water = this.waterResource.value();
      if (water) this.waterTitle.set(water.title);
    });

    effect(() => {
      const accommodation = this.accommodationResource.value();
      if (accommodation) this.patchForm(accommodation);
    });

    effect(() => {
      if (this.accommodationResource.error()) {
        this.toastService.showError(this.translate.instant('waters.accommodations.load-accommodation-failed'));
        this.navigateBack();
      }
    });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const accommodationIdParam = this.route.snapshot.paramMap.get('accommodationId');
    this.waterIdSig.set(Number(idParam));
    if (accommodationIdParam) this.accommodationIdSig.set(Number(accommodationIdParam));
  }

  private patchForm(accommodation: AccommodationDetail): void {
    this.accommodationTitle.set(accommodation.name ?? `#${accommodation.number ?? accommodation.id}`);
    this.imageName.set(accommodation.imageName);
    this.imageUrl.set(accommodation.imageUrl);
    this.form.patchValue({
      number: accommodation.number,
      persons: accommodation.persons,
      spots: accommodation.spots,
      minimumFishermen: accommodation.minimumFishermen,
      coTravelers: accommodation.coTravelers,
      exclusive: accommodation.exclusive,
      isFishingSpot: accommodation.isFishingSpot,
      isActive: accommodation.isActive,
      dogsAllowed: accommodation.dogsAllowed === null ? 'inherit' : accommodation.dogsAllowed,
      latitude: accommodation.locationLat,
      longitude: accommodation.locationLong,
      descriptionNl: accommodation.descriptionNl,
      descriptionEn: accommodation.descriptionEn,
      descriptionDe: accommodation.descriptionDe,
      descriptionIt: accommodation.descriptionIt,
      descriptionFr: accommodation.descriptionFr,
    });
    this.patchNames(accommodation.names);
  }

  private patchNames(names: AccommodationName[]): void {
    for (const name of names) {
      const key = String(name.siteId);
      if (this.namesGroup.contains(key)) {
        this.namesGroup.controls[key].setValue(name.name);
      }
    }
  }

  onImageFileSelected(file: File): void {
    this.pendingImageFile.set(file);
  }

  async save(): Promise<void> {
    // Validate the combination-pricing grid too (each cell must have both prices or neither).
    const pricingValid = this.combinationPricing()?.validate() ?? true;
    if (this.form.invalid || !pricingValid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const names: AccommodationName[] = Object.entries(this.namesGroup.controls).map(([siteId, ctrl]) => ({
      siteId: Number(siteId),
      name: ctrl.value || null,
    }));

    const dto: AccommodationCreateOrUpdateRequest = {
      number: raw.number,
      persons: raw.persons,
      spots: raw.spots,
      minimumFishermen: raw.minimumFishermen,
      coTravelers: raw.coTravelers,
      exclusive: raw.exclusive,
      isFishingSpot: raw.isFishingSpot,
      isActive: raw.isActive,
      dogsAllowed: raw.dogsAllowed === 'inherit' ? null : raw.dogsAllowed,
      locationLat: raw.latitude,
      locationLong: raw.longitude,
      descriptionNl: raw.descriptionNl || null,
      descriptionEn: raw.descriptionEn || null,
      descriptionDe: raw.descriptionDe || null,
      descriptionIt: raw.descriptionIt || null,
      descriptionFr: raw.descriptionFr || null,
      names,
    };

    this.isSaving.set(true);
    const accommodationId = this.accommodationIdSig();
    const waterId = this.waterIdSig();

    const request$ =
      this.isEditMode() && accommodationId !== null
        ? this.accommodationsService.updateAccommodation(waterId, accommodationId, dto)
        : this.accommodationsService.createAccommodation(waterId, dto);

    // Stage 1 — persist the accommodation itself (create or update).
    let accommodation: AccommodationDetail;
    try {
      accommodation = await firstValueFrom(request$);
    } catch (err) {
      const key =
        err instanceof HttpErrorResponse && err.error?.code === 'ACCOMMODATION_NUMBER_TAKEN'
          ? 'waters.accommodations.save-failed-number-taken'
          : 'waters.accommodations.save-failed';
      this.toastService.showError(this.translate.instant(key));
      this.isSaving.set(false);
      return;
    }

    // Stage 2 — fold in the combination-pricing grid. In create mode the accommodation only gets an id
    // here, so pass it through — the child PUTs the grid against the just-created unit.
    try {
      await firstValueFrom(this.combinationPricing()?.save(accommodation.id) ?? of(null));
    } catch {
      // The accommodation itself saved; only the pricing PUT failed. Adopt the new id so a retry
      // updates this unit instead of creating a duplicate (no-op if we were already editing).
      this.accommodationIdSig.set(accommodation.id);
      this.toastService.showError(this.translate.instant('waters.combination-prices.save-failed'));
      this.isSaving.set(false);
      return;
    }

    const successKey = this.isEditMode()
      ? 'waters.accommodations.save-success-edit'
      : 'waters.accommodations.save-success-create';
    this.toastService.showSuccess(this.translate.instant(successKey));

    // Stage 3 — upload a pending image if one was picked. A failure here is non-fatal: the save already
    // succeeded, so warn but still navigate back.
    const pendingFile = this.pendingImageFile();
    if (pendingFile) {
      try {
        await firstValueFrom(this.accommodationsService.uploadImage(waterId, accommodation.id, pendingFile));
        this.pendingImageFile.set(null);
        this.imagePicker()?.clearPending();
      } catch {
        this.toastService.showError(this.translate.instant('waters.accommodations.upload-failed'));
      }
    }

    this.isSaving.set(false);
    this.navigateBack();
  }

  async delete(): Promise<void> {
    const accommodationId = this.accommodationIdSig();
    if (!accommodationId) return;

    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.accommodations.delete-title'),
      prompt: this.translate.instant('waters.accommodations.delete-confirm'),
    });
    if (!confirmed) return;

    this.isDeleting.set(true);
    try {
      await firstValueFrom(this.accommodationsService.deleteAccommodation(this.waterIdSig(), accommodationId));
      this.toastService.showSuccess(this.translate.instant('waters.accommodations.delete-success-form'));
      this.navigateBack();
    } catch {
      this.toastService.showError(this.translate.instant('waters.accommodations.delete-failed-form'));
    } finally {
      this.isDeleting.set(false);
    }
  }

  cancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    void this.router.navigate(['/waters/edit', this.waterIdSig()], { queryParams: { tab: 3 } });
  }
}
